import type { HierarchyLoaderData } from "functions/api/hierarchy/hierarchyLoader";
import { useEffect } from "react";
import { useLoaderData } from "react-router-dom";
import { AppSettingsContext, useAppSettings } from "./AppSettingsContext";
import { getHierarchyBaseUrl } from "./hierarchy/getHierarchyBaseUrl";
import { LOAD_HIERARCHY } from "./hierarchy/interface";
import { useHierarchyEffects } from "./hierarchy/useHierarchyEffects";

interface HierarchyContextProviderProps {
  children: React.ReactNode;
}

/**
 * Thin adapter provider for hierarchy context.
 * Applies route loader data into the unified {@link AppSettingsContext} reducer; consumers now
 * read hierarchy state via the `useHierarchyContext` hook rather than a dedicated context.
 * All other hierarchy side effects (saving, SignalR listeners) are handled by
 * {@link useHierarchyEffects}; this component only applies route loader data, since that must
 * stay scoped to the hierarchy/ToC routes it is rendered within.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 */
export const HierarchyContextProvider = ({ children }: HierarchyContextProviderProps) => {
  const { hierarchyState: state, dispatch } = useAppSettings();
  const hierarchyLoaderData = useLoaderData() as HierarchyLoaderData;
  const { setCurrentBaseUrl } = useHierarchyEffects();

  /**
   * Add loader data into context
   * Check if the loader data is valid and if the hierarchy has changed
   */
  useEffect(() => {
    if (
      hierarchyLoaderData &&
      hierarchyLoaderData.hierarchy &&
      hierarchyLoaderData.accessRights &&
      hierarchyLoaderData.parentHierarchies &&
      // Reload if the Hierarchy is changed even if the name change is not saved
      (!state.isNameChanged ||
        !state.hierarchy ||
        state.hierarchy.id !== hierarchyLoaderData.hierarchy.id) &&
      (!state.hierarchy ||
        getHierarchyBaseUrl(state.hierarchy) !== getHierarchyBaseUrl(hierarchyLoaderData.hierarchy))
    ) {
      dispatch({
        operation: LOAD_HIERARCHY,
        hierarchy: hierarchyLoaderData.hierarchy,
        hierarchyNodes: hierarchyLoaderData.parentHierarchies,
        accessRights: hierarchyLoaderData.accessRights,
        isSubscribed: hierarchyLoaderData.isHierarchySubscribed,
      });
      setCurrentBaseUrl(getHierarchyBaseUrl(hierarchyLoaderData.hierarchy));
    }
  }, [
    dispatch,
    hierarchyLoaderData,
    setCurrentBaseUrl,
    state.hierarchy,
    state.id,
    state.isNameChanged,
  ]);

  return (
    <div
      id="hierarchyContextProvider"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      {children}
    </div>
  );
};

HierarchyContextProvider.displayName = "HierarchyContextProvider";
